/**
 * RWBR — THE CHAMPIONSHIP, DERIVED.
 *
 * Every number in this file is a FOLD OVER THE EVENT STREAM. Nothing is tallied
 * alongside the race as it runs and nothing is stored twice. That is the whole
 * point of `TELEMETRY_SPEC.md`, and it is not an aesthetic preference:
 *
 *   "Season totals are a fold over the event stream, not a separate set of
 *    numbers that can drift out of sync with the race log. That's exactly the
 *    class of bug that bit Season Points 3, where the season totals silently
 *    dropped race 9 of every band because the sums were hand-written cell
 *    references. Derive, never duplicate."                — TELEMETRY_SPEC
 *
 * So the season loop emits FACTS — this driver started 14th, finished 3rd, led
 * 6 turns, led the most turns, was still running at the flag, drew a points
 * forfeit — and this module turns facts into points, records and rankings. If
 * you want a new counter next season, you add a reader here and backfill from
 * the ledger; you never have to re-run a race.
 *
 * SCORING IS D-023 — EZRA's system, 48 maximum:
 *   • finishing position   43 down to 1     (1st = 43 … 43rd = 1)
 *   • race win             +3
 *   • led any turn         +1
 *   • led the most turns   +1 more
 *   • two double-points races a season: ONLY the base doubles. The win bonus
 *     stays 3 and the turn points stay 1 and 1.
 *
 * `Season Points 3.xlsx` implements 47 max — EZRA's system minus the
 * most-turns-led point — which is why the workbook is not the reference for
 * scoring (D-023).
 *
 * TV EVENT X 13 forfeits a driver's championship points FOR THAT RACE (D-028
 * §3). He still races and still takes the money; he scores nothing. The
 * forfeited total is kept, because "how much did that cost him" is a story.
 */

import type { RuleEvent } from './ruleEvent.ts';
import { modifierValue } from './ruleEvent.ts';

// ---------------------------------------------------------------------------
// Citations
// ---------------------------------------------------------------------------

export const CITE_POINTS = 'D-023 — EZRA scoring, 48 maximum (43 down to 1, +3 win, +1 led, +1 led most)';
export const CITE_DOUBLE_POINTS = 'D-023 — "only the base points double; the win bonus stays 3 and the turn points stay 1 and 1"';
export const CITE_POINTS_FORFEIT = 'D-028 §3 / RWEG/p.8 — TV EVENT X 13: forfeits the championship points earned in this race';
export const CITE_TIEBREAK = 'D-012 — "ties broken on average points per start"';
export const CITE_ESCAPE_RATE = 'SAG/8 — next season\'s ESCAPE rating is the driver\'s actual percentage of TROUBLE rolls escaped';
export const CITE_RAF = 'N-007 — RAF% = races finished / races entered (recovered from RWB Prerace randomizer II.xlsm)';
export const CITE_HEAT = 'SAG/7 (N-008) — HEAT goes to the top 25% by instances of initiating a duel, being in a fight, in an altercation, during the Race Weeks and the races';
export const CITE_TROPHY_AVG_START = 'SAG (N-010) — Trophy from average start for drivers with no DNQ';
export const CITE_DNF_ZERO = 'D-EZ-98 house rule — Season Points 3: a DNF scores no finishing points';
export const CITE_STAGE_POINTS =
  'D-EZ-98 house rule — Season Points 3 "Stage Pts": the race in thirds; top ten at the end of stages one and two score 10 down to 1';
export const CITE_WON_BY = 'SAG/24 — won on the last turn with a SURGE or a DUEL';

// ---------------------------------------------------------------------------
// The season ledger envelope
// ---------------------------------------------------------------------------

/** Which part of the week an event came out of. */
export type SeasonPhase = 'race-week' | 'race' | 'season';

/**
 * A `RuleEvent` with the season coordinates the fold needs. Race Week events,
 * race turn events and the season loop's own events all wear this.
 */
export interface SeasonEvent extends RuleEvent {
  seasonId: string;
  /** 1-36. */
  raceNumber: number;
  raceId: string;
  phase: SeasonPhase;
  /** The race turn, where the event had one. */
  turn: number | null;
  /** Position in the season stream. Append-only, so this never changes. */
  sequence: number;
}

/** `eventType` for everything the season loop itself emits. */
export const SEASON_EVENT_TYPE = 'season';

/** How a driver's attempt at a race ended before the green flag. */
export type EntryStatus = 'start' | 'dnq' | 'missed-the-show';

/** Why a driver was not at the track at all. */
export type AbsenceReason = 'suspended' | 'injured' | 'season-injury' | 'dead' | 'quit' | 'no-ride';

// ---------------------------------------------------------------------------
// Reading modifiers back out
// ---------------------------------------------------------------------------

function mod(event: RuleEvent, name: string): number | string | undefined {
  return event.modifiers.find((m) => m.name === name)?.value;
}

function numMod(event: RuleEvent, name: string): number | null {
  const found = event.modifiers.find((m) => m.name === name);
  if (!found) return null;
  return modifierValue(found);
}

function strMod(event: RuleEvent, name: string): string | null {
  const value = mod(event, name);
  return typeof value === 'string' ? value : null;
}

function flagMod(event: RuleEvent, name: string): boolean {
  return (numMod(event, name) ?? 0) > 0;
}

// ---------------------------------------------------------------------------
// D-023 — the points formula
// ---------------------------------------------------------------------------

/** 1st place is worth this; every place back is worth one less, to a floor of 1. */
export const POINTS_FOR_FIRST = 43;
export const WIN_BONUS = 3;
export const LED_A_TURN_BONUS = 1;
export const LED_MOST_TURNS_BONUS = 1;
/** 43 + 3 + 1 + 1. A driver cannot score more than this in a normal race. */
export const MAX_RACE_POINTS = POINTS_FOR_FIRST + WIN_BONUS + LED_A_TURN_BONUS + LED_MOST_TURNS_BONUS;

export interface RaceScoreInput {
  /** 1-based finishing position. */
  finishPosition: number;
  turnsLed: number;
  ledMostTurns: boolean;
  doublePoints: boolean;
  /** TV EVENT X 13 — he scores nothing this week. */
  forfeited: boolean;
  /**
   * D-EZ-98 HOUSE RULE, off Howard's own sheet ("DNF scores 0"): a car that was
   * not running at the flag scores no FINISHING-POSITION points. What he earned
   * by leading turns is kept — he did lead them. Off by default; the base game
   * (D-023) scores a DNF on its classified position.
   */
  dnfScoresZero?: boolean;
  /** Only read when `dnfScoresZero` is on. Defaults to true. */
  runningAtFinish?: boolean;
}

export interface RaceScore {
  base: number;
  winBonus: number;
  ledBonus: number;
  mostLedBonus: number;
  /** What he earned before the forfeit is applied. */
  earned: number;
  /** What the forfeit took off him — 0 or `earned`. */
  forfeited: number;
  /** What actually goes on the board. */
  awarded: number;
}

/**
 * D-023. The finishing ladder runs 43 down to 1 across the maximum 43-car
 * field; anything classified below 43rd (which the boards cannot produce) still
 * scores the floor of 1 rather than going negative.
 */
export function scoreRace(input: RaceScoreInput): RaceScore {
  if (!Number.isInteger(input.finishPosition) || input.finishPosition < 1) {
    throw new RangeError(`a finishing position must be a positive whole number, got ${input.finishPosition}`);
  }
  const zeroed = input.dnfScoresZero === true && input.runningAtFinish === false;
  const ladder = zeroed ? 0 : Math.max(1, POINTS_FOR_FIRST + 1 - input.finishPosition);
  const base = input.doublePoints ? ladder * 2 : ladder;
  const winBonus = input.finishPosition === 1 ? WIN_BONUS : 0;
  const ledBonus = input.turnsLed > 0 ? LED_A_TURN_BONUS : 0;
  const mostLedBonus = input.ledMostTurns ? LED_MOST_TURNS_BONUS : 0;
  const earned = base + winBonus + ledBonus + mostLedBonus;
  const forfeited = input.forfeited ? earned : 0;
  return { base, winBonus, ledBonus, mostLedBonus, earned, forfeited, awarded: earned - forfeited };
}

// ---------------------------------------------------------------------------
// The events the season loop emits
// ---------------------------------------------------------------------------

export interface EntryFacts {
  driverId: string;
  name: string;
  teamId: string | null;
  status: EntryStatus;
  /** 1-based grid position. Only meaningful when `status` is `start`. */
  startPosition: number | null;
}

/**
 * ONE PER DRIVER WHO TURNED UP, whatever became of him. This is the event that
 * makes "races entered" and "DNQ" derivable rather than remembered, and it
 * carries the driver's name and team as of that week so the fold never needs a
 * roster handed to it.
 */
export function entryEvent(facts: EntryFacts): RuleEvent {
  const where =
    facts.status === 'start'
      ? `starts ${facts.startPosition}${ordinalSuffix(facts.startPosition ?? 0)}`
      : facts.status === 'dnq'
        ? 'fails to qualify'
        : 'is left on the bubble and does not race';
  return {
    eventType: SEASON_EVENT_TYPE,
    momentTag: 'season.entry',
    ruleCitation: 'RWEG/p.4 STEP SIX — the field is rounded out to 43; everyone else goes home',
    actorIds: [facts.driverId],
    positionDeltas: [],
    modifiers: [
      { name: 'driver-name', value: facts.name },
      { name: 'team', value: facts.teamId ?? 'independent' },
      { name: 'entry-status', value: facts.status },
      ...(facts.startPosition === null
        ? []
        : [{ name: 'start-position', value: facts.startPosition, note: 'grid slot off Race Week' }]),
    ],
    dice: [],
    description: `${facts.name} ${where}.`,
  };
}

export interface ResultFacts {
  driverId: string;
  name: string;
  teamId: string | null;
  finishPosition: number;
  turnsLed: number;
  ledMostTurns: boolean;
  /** False for a car that was wrecked, blown up or black-flagged out. */
  runningAtFinish: boolean;
  outReason: string | null;
  doublePoints: boolean;
  pointsForfeited: boolean;
  /**
   * D-EZ-98 — the league scored this race with "DNF scores 0" switched on. The
   * rule in force is recorded ON THE FACT, so the fold needs no options and a
   * season can never be re-scored under a rule it was not raced under.
   */
  dnfScoresZero?: boolean;
  /** The winner took the lead on the final turn with a SURGE or a DUEL (SAG/24). */
  wonBy?: 'surge' | 'duel' | null;
}

/**
 * ONE PER STARTER. Facts only — no points. `foldSeason` applies D-023 to these,
 * which is why the championship cannot disagree with the race log.
 */
export function resultEvent(facts: ResultFacts): RuleEvent {
  const score = scoreRace({
    finishPosition: facts.finishPosition,
    turnsLed: facts.turnsLed,
    ledMostTurns: facts.ledMostTurns,
    doublePoints: facts.doublePoints,
    forfeited: facts.pointsForfeited,
    dnfScoresZero: facts.dnfScoresZero,
    runningAtFinish: facts.runningAtFinish,
  });
  return {
    eventType: SEASON_EVENT_TYPE,
    momentTag: 'season.result',
    ruleCitation: CITE_POINTS,
    actorIds: [facts.driverId],
    positionDeltas: [],
    modifiers: [
      { name: 'driver-name', value: facts.name },
      { name: 'team', value: facts.teamId ?? 'independent' },
      { name: 'finish-position', value: facts.finishPosition },
      { name: 'turns-led', value: facts.turnsLed },
      { name: 'led-most-turns', value: facts.ledMostTurns ? 1 : 0 },
      { name: 'running-at-finish', value: facts.runningAtFinish ? 1 : 0 },
      ...(facts.outReason ? [{ name: 'out-reason', value: facts.outReason }] : []),
      { name: 'double-points', value: facts.doublePoints ? 1 : 0, note: CITE_DOUBLE_POINTS },
      ...(facts.pointsForfeited
        ? [{ name: 'points-forfeited', value: 1, note: CITE_POINTS_FORFEIT }]
        : []),
      ...(facts.dnfScoresZero ? [{ name: 'dnf-scores-zero', value: 1, note: CITE_DNF_ZERO }] : []),
      ...(facts.wonBy ? [{ name: 'won-by', value: facts.wonBy, note: CITE_WON_BY }] : []),
    ],
    dice: [],
    // The points are narrated, not stored: the number below is recomputed by
    // the fold from the facts above, so the sentence and the standings can
    // never say different things.
    description:
      `${facts.name} finishes ${facts.finishPosition}${ordinalSuffix(facts.finishPosition)}` +
      `${facts.turnsLed > 0 ? `, leading ${facts.turnsLed} turn${facts.turnsLed === 1 ? '' : 's'}` : ''}` +
      `${facts.runningAtFinish ? '' : ` (out — ${facts.outReason ?? 'unknown'})`}` +
      `${facts.pointsForfeited ? ' — and forfeits every point of it.' : ` for ${score.awarded} points.`}`,
  };
}

/** A driver who was not at the track at all. Keeps "races missed" derivable. */
export function absenceEvent(
  driverId: string,
  name: string,
  reason: AbsenceReason,
  citation: string,
  note?: string,
): RuleEvent {
  return {
    eventType: SEASON_EVENT_TYPE,
    momentTag: `season.absent.${reason}`,
    ruleCitation: citation,
    actorIds: [driverId],
    positionDeltas: [],
    modifiers: [
      { name: 'driver-name', value: name },
      { name: 'absence-reason', value: reason },
      ...(note ? [{ name: 'detail', value: note }] : []),
    ],
    dice: [],
    description: `${name} does not enter — ${note ?? reason}.`,
  };
}

/** Points for 1st..10th at the end of a stage (D-EZ-98). */
export const STAGE_POINTS: readonly number[] = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1];

/**
 * D-EZ-98 — the running order at the end of a stage, as a FACT. `order` is the
 * top ten (or fewer) still running when the stage ended, leader first.
 * `counts` records whether the league adds stage points to the championship;
 * the fold tracks them either way and only adds them when this says so.
 */
export function stageEvent(args: {
  stage: number;
  endTurn: number;
  order: readonly { driverId: string; name: string }[];
  counts: boolean;
}): RuleEvent {
  const top = args.order.slice(0, STAGE_POINTS.length);
  return {
    eventType: SEASON_EVENT_TYPE,
    momentTag: 'season.stage',
    ruleCitation: CITE_STAGE_POINTS,
    actorIds: top.map((d) => d.driverId),
    positionDeltas: [],
    modifiers: [
      { name: 'stage', value: args.stage },
      { name: 'end-turn', value: args.endTurn },
      { name: 'stage-points-count', value: args.counts ? 1 : 0 },
      ...top.map((d, i) => ({ name: `stage-p${i + 1}`, value: STAGE_POINTS[i], note: d.name })),
    ],
    dice: [],
    description:
      top.length > 0
        ? `Stage ${args.stage} ends at turn ${args.endTurn}: ${top[0].name} takes it.`
        : `Stage ${args.stage} ends at turn ${args.endTurn} with nobody running.`,
  };
}

function ordinalSuffix(n: number): string {
  const rem100 = n % 100;
  if (rem100 >= 11 && rem100 <= 13) return 'th';
  const rem10 = n % 10;
  return rem10 === 1 ? 'st' : rem10 === 2 ? 'nd' : rem10 === 3 ? 'rd' : 'th';
}

// ---------------------------------------------------------------------------
// What a season adds up to
// ---------------------------------------------------------------------------

/**
 * Every counter here declares its consumer, per TELEMETRY_SPEC's governing
 * rule. `RERATE` means the off-season formula cannot run without it.
 */
export interface DriverSeasonStats {
  driverId: string;
  name: string;
  /** The team he raced for most recently this season. */
  teamId: string | null;

  /** `RECORD` — the championship. */
  points: number;
  /** Points awarded in each race he scored in, keyed by race number. */
  pointsByRace: Record<number, number>;
  /** `STORY` — what TV EVENT X 13 cost him. */
  pointsForfeited: number;

  /** `RERATE` — races he turned up for, whether or not he made the show. */
  entries: number;
  /** `RERATE` — races he started. Sagliano's "races entered" for RAF%. */
  starts: number;
  /** `RERATE` — Trophy re-rate switches tables for any driver with a DNQ (N-010). */
  dnqs: number;
  /** Made the entry list but was left on the bubble (RWEG/p.4). */
  missedTheShow: number;
  /** Suspended, injured, dead or without a ride. */
  racesMissed: number;

  /** `RECORD` */
  wins: number;
  poles: number;
  top5: number;
  top10: number;
  /** `RECORD` + `MONEY` — a turn is 10 miles (p.ii), not a lap. */
  turnsLed: number;
  /** Races in which he led at least one turn — the +1 in D-023. */
  racesLed: number;
  /** Races in which he led the most turns — the second +1 in D-023. */
  mostTurnsLedRaces: number;

  /** `RERATE` — the 75% demotion rule and RAF%. */
  racesFinished: number;
  dnfs: number;
  /** DNFs split by `Car.outReason`: trouble / problem / feud / fuel / black-flag / repair-failed. */
  dnfsByCause: Record<string, number>;
  /** `RERATE` — races finished / races started. Null before he starts one. */
  rafPct: number | null;

  /** `RERATE` — Trophy assignment (<=10 Gold, <=11 Silver, <=22 Bronze). */
  avgStart: number | null;
  avgFinish: number | null;
  bestFinish: number | null;
  worstFinish: number | null;

  /** `RERATE` — next season's ESCAPE rating IS this percentage (SAG/8). */
  troubleAttempts: number;
  troubleEscapes: number;
  /** Chips spent to buy out of trouble: not an escape, and not a collection. */
  troubleChipSaves: number;
  troubleCollected: number;
  escapePct: number | null;

  /** `RERATE` — HEAT goes to the top 25% by aggression (SAG/7, N-008). */
  duelsInstigated: number;
  duelsWon: number;
  duelsLost: number;
  /** Duels + feud/fume blow-ups + Race Week altercations. The HEAT input. */
  heatIncidents: number;
  /** `STORY` — races he went into with somebody. */
  feudRaces: number;

  /**
   * `RECORD` + `MONEY` — wins taken on the final turn by a SURGE or a DUEL
   * (SAG/24's "last game turn, Surge or DUEL used to win the race"). Howard's
   * sheet counts these as their own column, "Won by Surge or Duel"; the
   * contingency award was always paid, and now the win is counted too.
   */
  winsBySurge: number;
  winsByDuel: number;

  /**
   * D-EZ-98 — STAGE POINTS, a house rule off Howard's sheet. Each race is cut
   * into thirds of its turns; the top ten running at the end of each of the
   * first two stages score 10 down to 1. Tracked here whether or not they count;
   * they are added to `points` only when the league switches them on.
   */
  stagePoints: number;
  /** Stages won (first at the end of a stage). */
  stageWins: number;

  /** D-012's tiebreak. */
  avgPointsPerStart: number;
  /**
   * 1-based CHAMPIONSHIP rank, after sorting. Mid-season it is the points order.
   * In a finished season's `finalStandings` it is the PLAYOFF order (D-EZ-90):
   * the champion first, then the final four, then each round's eliminees, then
   * everybody else on points. Points stay points — see `pointsRank`.
   */
  rank: number;
  /**
   * 1-based rank on POINTS ALONE. Equal to `rank` until the playoff reorders
   * the final table; kept so a screen can still say "most points" honestly.
   */
  pointsRank: number;
}

export interface TeamSeasonStats {
  teamId: string;
  points: number;
  starts: number;
  wins: number;
  poles: number;
  top5: number;
  top10: number;
  dnfs: number;
  turnsLed: number;
  driverIds: string[];
  rank: number;
}

export interface SeasonStandings {
  /** The highest race number this fold saw. 0 for an empty ledger. */
  throughRace: number;
  /** Every race number that contributed, ascending. */
  racesCounted: number[];
  /** Drivers in championship order. */
  drivers: DriverSeasonStats[];
  byDriver: Record<string, DriverSeasonStats>;
  teams: TeamSeasonStats[];
  byTeam: Record<string, TeamSeasonStats>;
}

// ---------------------------------------------------------------------------
// The fold
// ---------------------------------------------------------------------------

/** The bits of `TroubleResult` the fold needs. Structural, so it cannot drift. */
interface TroubleCheckShape {
  driverId?: unknown;
  escaped?: unknown;
  collected?: unknown;
  chipSpent?: unknown;
}

interface TroubleShape {
  checks?: unknown;
}

/** The bits of `DuelResult` the fold needs. */
interface DuelShape {
  instigatorId?: unknown;
  targetId?: unknown;
  declined?: unknown;
  won?: unknown;
}

function blankDriver(driverId: string): DriverSeasonStats {
  return {
    driverId,
    name: driverId,
    teamId: null,
    points: 0,
    pointsByRace: {},
    pointsForfeited: 0,
    entries: 0,
    starts: 0,
    dnqs: 0,
    missedTheShow: 0,
    racesMissed: 0,
    wins: 0,
    poles: 0,
    top5: 0,
    top10: 0,
    turnsLed: 0,
    racesLed: 0,
    mostTurnsLedRaces: 0,
    racesFinished: 0,
    dnfs: 0,
    dnfsByCause: {},
    rafPct: null,
    avgStart: null,
    avgFinish: null,
    bestFinish: null,
    worstFinish: null,
    troubleAttempts: 0,
    troubleEscapes: 0,
    troubleChipSaves: 0,
    troubleCollected: 0,
    escapePct: null,
    duelsInstigated: 0,
    duelsWon: 0,
    duelsLost: 0,
    heatIncidents: 0,
    feudRaces: 0,
    winsBySurge: 0,
    winsByDuel: 0,
    stagePoints: 0,
    stageWins: 0,
    avgPointsPerStart: 0,
    rank: 0,
    pointsRank: 0,
  };
}

export interface FoldOptions {
  /**
   * Only fold these race numbers. This is how a playoff round's points are
   * read: the same fold, over a slice of the same stream. There is no second
   * scoring path anywhere in this project.
   */
  races?: readonly number[];
  /** Only fold these drivers. Everything else is skipped entirely. */
  drivers?: readonly string[];
}

/**
 * Turn a season's event stream into standings.
 *
 * Pure, total and order-independent for totals: feeding the same events in a
 * different order gives the same numbers (only `teamId`, which is "the last
 * team he drove for", depends on order, and that is read off `sequence`).
 */
export function foldSeason(events: readonly SeasonEvent[], options: FoldOptions = {}): SeasonStandings {
  const raceFilter = options.races ? new Set(options.races) : null;
  const driverFilter = options.drivers ? new Set(options.drivers) : null;

  const drivers = new Map<string, DriverSeasonStats>();
  const teamAt = new Map<string, { sequence: number; teamId: string | null }>();
  const startSum = new Map<string, number>();
  const finishSum = new Map<string, number>();
  const feudSeen = new Map<string, Set<number>>();
  const racesCounted = new Set<number>();

  const want = (driverId: string): DriverSeasonStats | null => {
    if (driverFilter && !driverFilter.has(driverId)) return null;
    let stat = drivers.get(driverId);
    if (!stat) {
      stat = blankDriver(driverId);
      drivers.set(driverId, stat);
    }
    return stat;
  };

  const noteTeam = (stat: DriverSeasonStats, event: SeasonEvent): void => {
    const raw = strMod(event, 'team');
    if (raw === null) return;
    const teamId = raw === 'independent' ? null : raw;
    const held = teamAt.get(stat.driverId);
    if (!held || event.sequence >= held.sequence) {
      teamAt.set(stat.driverId, { sequence: event.sequence, teamId });
      stat.teamId = teamId;
    }
  };

  for (const event of events) {
    if (raceFilter && !raceFilter.has(event.raceNumber)) continue;
    racesCounted.add(event.raceNumber);

    // -- what the season loop said -------------------------------------------
    if (event.eventType === SEASON_EVENT_TYPE && event.momentTag === 'season.stage') {
      const counts = flagMod(event, 'stage-points-count');
      event.actorIds.forEach((id, i) => {
        const stat = want(id);
        if (!stat) return;
        const pts = STAGE_POINTS[i] ?? 0;
        stat.stagePoints += pts;
        if (i === 0) stat.stageWins++;
        if (counts && pts > 0) {
          stat.points += pts;
          stat.pointsByRace[event.raceNumber] = (stat.pointsByRace[event.raceNumber] ?? 0) + pts;
        }
      });
      continue;
    }

    if (event.eventType === SEASON_EVENT_TYPE) {
      const driverId = event.actorIds[0];
      if (!driverId) continue;
      const stat = want(driverId);
      if (!stat) continue;
      const name = strMod(event, 'driver-name');
      if (name) stat.name = name;
      noteTeam(stat, event);

      if (event.momentTag === 'season.entry') {
        const status = strMod(event, 'entry-status') as EntryStatus | null;
        stat.entries++;
        if (status === 'dnq') stat.dnqs++;
        else if (status === 'missed-the-show') stat.missedTheShow++;
        else if (status === 'start') {
          stat.starts++;
          const grid = numMod(event, 'start-position') ?? 0;
          if (grid > 0) {
            startSum.set(driverId, (startSum.get(driverId) ?? 0) + grid);
            if (grid === 1) stat.poles++;
          }
        }
        continue;
      }

      if (event.momentTag === 'season.result') {
        const place = numMod(event, 'finish-position') ?? 0;
        const turnsLed = numMod(event, 'turns-led') ?? 0;
        const ledMost = flagMod(event, 'led-most-turns');
        const running = flagMod(event, 'running-at-finish');
        const doublePoints = flagMod(event, 'double-points');
        const forfeited = flagMod(event, 'points-forfeited');
        const score = scoreRace({
          finishPosition: place,
          turnsLed,
          ledMostTurns: ledMost,
          doublePoints,
          forfeited,
          dnfScoresZero: flagMod(event, 'dnf-scores-zero'),
          runningAtFinish: running,
        });

        stat.points += score.awarded;
        stat.pointsByRace[event.raceNumber] = (stat.pointsByRace[event.raceNumber] ?? 0) + score.awarded;
        stat.pointsForfeited += score.forfeited;
        stat.turnsLed += turnsLed;
        if (turnsLed > 0) stat.racesLed++;
        if (ledMost) stat.mostTurnsLedRaces++;
        if (place === 1) {
          stat.wins++;
          const wonBy = strMod(event, 'won-by');
          if (wonBy === 'surge') stat.winsBySurge++;
          else if (wonBy === 'duel') stat.winsByDuel++;
        }
        if (place <= 5) stat.top5++;
        if (place <= 10) stat.top10++;
        finishSum.set(driverId, (finishSum.get(driverId) ?? 0) + place);
        stat.bestFinish = stat.bestFinish === null ? place : Math.min(stat.bestFinish, place);
        stat.worstFinish = stat.worstFinish === null ? place : Math.max(stat.worstFinish, place);
        if (running) {
          stat.racesFinished++;
        } else {
          stat.dnfs++;
          const cause = strMod(event, 'out-reason') ?? 'unknown';
          stat.dnfsByCause[cause] = (stat.dnfsByCause[cause] ?? 0) + 1;
        }
        continue;
      }

      if (event.momentTag.startsWith('season.absent.')) {
        stat.racesMissed++;
        continue;
      }
      continue;
    }

    // -- TROUBLE: the ESCAPE re-rate's raw material (SAG/8) -------------------
    //
    // Every escape roll counts, including the ones inside a duel and inside a
    // feud, because they all come through the same trouble module and all land
    // in the same ledger. TELEMETRY_SPEC calls this "the single most elegant
    // loop in the guide"; it only works if nothing is missed.
    if (event.eventType === 'trouble') {
      const checks = (event as unknown as TroubleShape).checks;
      if (!Array.isArray(checks)) continue;
      for (const raw of checks as TroubleCheckShape[]) {
        if (typeof raw.driverId !== 'string') continue;
        const stat = want(raw.driverId);
        if (!stat) continue;
        stat.troubleAttempts++;
        if (raw.escaped === true) stat.troubleEscapes++;
        if (raw.chipSpent === true) stat.troubleChipSaves++;
        if (raw.collected === true) stat.troubleCollected++;
      }
      continue;
    }

    // -- DUELS and FEUDS: the HEAT re-rate's raw material (SAG/7, N-008) ------
    if (event.eventType === 'duel') {
      const duel = event as unknown as DuelShape;
      if (duel.declined === true) continue;
      if (typeof duel.instigatorId === 'string') {
        const stat = want(duel.instigatorId);
        if (stat) {
          stat.duelsInstigated++;
          stat.heatIncidents++;
          if (duel.won === true) stat.duelsWon++;
          else stat.duelsLost++;
        }
      }
      if (typeof duel.targetId === 'string') {
        // Being duelled is an instance of "being in a fight" even when you did
        // not start it — SAG/7 counts instances, not initiations only.
        const stat = want(duel.targetId);
        if (stat) stat.heatIncidents++;
      }
      continue;
    }

    if (event.eventType === 'feud-fume') {
      for (const id of event.actorIds) {
        const stat = want(id);
        if (!stat) continue;
        stat.heatIncidents++;
      }
      continue;
    }

    // -- RACE WEEK altercations ("during the Race Weeks", SAG/7) -------------
    if (event.eventType === 'race-week' && event.momentTag === 'raceweek.feud') {
      for (const id of event.actorIds) {
        const stat = want(id);
        if (!stat) continue;
        stat.heatIncidents++;
        let seen = feudSeen.get(id);
        if (!seen) {
          seen = new Set();
          feudSeen.set(id, seen);
        }
        if (!seen.has(event.raceNumber)) {
          seen.add(event.raceNumber);
          stat.feudRaces++;
        }
      }
      continue;
    }
  }

  // -- derived rates --------------------------------------------------------
  for (const stat of drivers.values()) {
    stat.avgStart = stat.starts > 0 ? (startSum.get(stat.driverId) ?? 0) / stat.starts : null;
    stat.avgFinish = stat.starts > 0 ? (finishSum.get(stat.driverId) ?? 0) / stat.starts : null;
    // N-007 recovered RAF% as races finished / races entered. "Entered" is read
    // here as STARTED, not "turned up": a driver who fails to qualify never
    // takes the green flag, so counting a DNQ against his finishing rate would
    // punish him twice — the Trophy re-rate already switches tables on a DNQ
    // (N-010). `entries` is kept separately for anyone who wants the other
    // denominator.
    // AMBIGUITY: the guide never spells the denominator out; the spreadsheet's
    // column heading is all we have.
    stat.rafPct = stat.starts > 0 ? stat.racesFinished / stat.starts : null;
    stat.escapePct = stat.troubleAttempts > 0 ? stat.troubleEscapes / stat.troubleAttempts : null;
    stat.avgPointsPerStart = stat.starts > 0 ? stat.points / stat.starts : 0;
  }

  const ordered = [...drivers.values()].sort(compareDrivers);
  ordered.forEach((stat, i) => {
    stat.rank = i + 1;
    stat.pointsRank = i + 1;
  });

  const teams = foldTeams(ordered);

  return {
    throughRace: racesCounted.size > 0 ? Math.max(...racesCounted) : 0,
    racesCounted: [...racesCounted].sort((a, b) => a - b),
    drivers: ordered,
    byDriver: Object.fromEntries(ordered.map((s) => [s.driverId, s])),
    teams,
    byTeam: Object.fromEntries(teams.map((t) => [t.teamId, t])),
  };
}

/**
 * D-012 — "ties broken on average points per start". Wins come after that, and
 * the driver id last, so the order is total even when two drivers had
 * identical seasons.
 */
export function compareDrivers(a: DriverSeasonStats, b: DriverSeasonStats): number {
  if (b.points !== a.points) return b.points - a.points;
  if (b.avgPointsPerStart !== a.avgPointsPerStart) return b.avgPointsPerStart - a.avgPointsPerStart;
  if (b.wins !== a.wins) return b.wins - a.wins;
  if (b.top5 !== a.top5) return b.top5 - a.top5;
  return a.driverId < b.driverId ? -1 : a.driverId > b.driverId ? 1 : 0;
}

function foldTeams(drivers: readonly DriverSeasonStats[]): TeamSeasonStats[] {
  const teams = new Map<string, TeamSeasonStats>();
  for (const stat of drivers) {
    if (!stat.teamId) continue;
    let team = teams.get(stat.teamId);
    if (!team) {
      team = {
        teamId: stat.teamId,
        points: 0,
        starts: 0,
        wins: 0,
        poles: 0,
        top5: 0,
        top10: 0,
        dnfs: 0,
        turnsLed: 0,
        driverIds: [],
        rank: 0,
      };
      teams.set(stat.teamId, team);
    }
    team.points += stat.points;
    team.starts += stat.starts;
    team.wins += stat.wins;
    team.poles += stat.poles;
    team.top5 += stat.top5;
    team.top10 += stat.top10;
    team.dnfs += stat.dnfs;
    team.turnsLed += stat.turnsLed;
    team.driverIds.push(stat.driverId);
  }
  const ordered = [...teams.values()].sort(
    (a, b) => b.points - a.points || b.wins - a.wins || (a.teamId < b.teamId ? -1 : 1),
  );
  ordered.forEach((team, i) => {
    team.rank = i + 1;
  });
  return ordered;
}

// ---------------------------------------------------------------------------
// D-EZ-90 — the champion is first
// ---------------------------------------------------------------------------

export const CITE_CHAMPION_FIRST =
  'D-EZ-90 — the playoff champion is listed first, then the final four, then each round\'s eliminees, then the rest on points';

/** The parts of a finished playoff bracket the final table is ordered by. */
export interface PlayoffOrderInput {
  championId: string | null;
  rounds: readonly {
    round: string;
    advanced: readonly string[];
    eliminated: readonly string[];
  }[];
}

/**
 * THE FINAL CHAMPIONSHIP TABLE, IN THE ORDER THE PLAYOFF DECIDED IT.
 *
 * Howard, 4 Oct 2026: the champion is listed first and gets everything that
 * comes with first. Everything downstream that reads a final rank — the title
 * bonus, the starred STAR, the re-rate pedals, royalty performance points,
 * sponsor renewals, the printed standings — reads `rank` off this table.
 *
 * THE ORDER, following the bracket's own shape (D-012: 16 → 12 → 8 → 4 → 1):
 *
 *   1.    the champion;
 *   2-4.  the rest of the championship four, in the order the title race
 *         ranked them (round points, then the finish in that race);
 *   5-8.  the drivers knocked out in the Round of 8, best season first;
 *   9-12. the Round of 12's eliminees, likewise;
 *   13-16 the Round of 16's;
 *   17+   everybody who missed the playoff, on points (D-012's tiebreaks).
 *
 * Inside an elimination round the order is SEASON points (`compareDrivers`),
 * not the round's own points — the round already did its job by deciding who
 * went out, and the season is the longer evidence.
 *
 * POINTS STAY POINTS. Nobody's total is touched; `pointsRank` keeps the order a
 * points table would have printed, so "most points" is still answerable.
 *
 * A season without a decided championship round (unfinished, or a test with
 * no playoff) is returned in points order unchanged.
 */
export function championshipOrder(standings: SeasonStandings, playoff: PlayoffOrderInput): SeasonStandings {
  const final = playoff.rounds.find((r) => r.round === 'championship');
  if (!final || !playoff.championId) return standings;

  const placed = new Set<string>();
  const order: DriverSeasonStats[] = [];
  const take = (id: string): void => {
    if (placed.has(id)) return;
    const stat = standings.byDriver[id];
    if (!stat) return;
    placed.add(id);
    order.push(stat);
  };

  take(playoff.championId);
  for (const id of [...final.advanced, ...final.eliminated]) take(id);

  // Later rounds first: going out in the Round of 8 beats going out in the 16.
  const earlier = playoff.rounds.filter((r) => r.round !== 'championship').reverse();
  for (const round of earlier) {
    const out = round.eliminated
      .map((id) => standings.byDriver[id])
      .filter((s): s is DriverSeasonStats => Boolean(s))
      .sort(compareDrivers);
    for (const stat of out) take(stat.driverId);
  }
  for (const stat of standings.drivers) take(stat.driverId);

  const drivers = order.map((stat, i) => ({ ...stat, rank: i + 1, pointsRank: stat.pointsRank || stat.rank }));
  return {
    ...standings,
    drivers,
    byDriver: Object.fromEntries(drivers.map((d) => [d.driverId, d])),
  };
}

// ---------------------------------------------------------------------------
// Reading standings back
// ---------------------------------------------------------------------------

/** Points a driver scored across a set of races. 0 if he scored in none. */
export function pointsOver(stat: DriverSeasonStats, races: readonly number[]): number {
  let total = 0;
  for (const race of races) total += stat.pointsByRace[race] ?? 0;
  return total;
}

/** The driver ids who won at least one race in this fold, in standings order. */
export function raceWinners(standings: SeasonStandings): string[] {
  return standings.drivers.filter((d) => d.wins > 0).map((d) => d.driverId);
}

export function standingsLine(stat: DriverSeasonStats): string {
  return (
    `${String(stat.rank).padStart(2)}  ${stat.name.padEnd(22)} ${String(stat.points).padStart(5)} pts  ` +
    `${stat.wins}W ${stat.top5}T5 ${stat.top10}T10  led ${stat.turnsLed} turns  ` +
    `avg start ${stat.avgStart === null ? '  -' : stat.avgStart.toFixed(1).padStart(4)}  ` +
    `avg fin ${stat.avgFinish === null ? '  -' : stat.avgFinish.toFixed(1).padStart(4)}  ` +
    `${stat.starts} starts, ${stat.dnfs} DNF, ${stat.dnqs} DNQ`
  );
}
